
import CheckAvailabilityProductSelector from "@dana/Components/CheckAvailability/CheckAvailabilityProductSelector";
import Availability from "@dana/Icons/Availability";
import { elevation, themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { fontSizes, lineHeights } from "@dana/MobiusOverrides/styles/typography";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconWrapper } from "@insite/mobius/Icon";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useState } from "react";
import { css } from "styled-components";

export interface CheckAvailabilityButtonStyles {
    wrapper?: InjectableCss;
    checkAvailabilityButton?: ButtonPresentationProps;
    checkAvailabilityModal?: ModalPresentationProps;
}

export const checkAvailabilityButtonStyles: CheckAvailabilityButtonStyles = {
    wrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: none;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    checkAvailabilityButton: {
        variant: "tertiary",
        mergeCss: true,
        icon: {
            src: Availability,
            position: "left",
        },
        css: css`
            display: flex;
            align-items: center;
            column-gap: 8px;
            > ${IconWrapper} {
                margin-right: 0;
                &,
                svg {
                    width: 18px;
                    height: 18px;
                }
            }

            &:hover,
            &:focus,
            &:focus-visible {
                svg {
                    path {
                        fill: ${themeColors.secondary.danaBrightBlue};
                    }
                }
                ${TypographyStyle} {
                    color: ${themeColors.secondary.danaBrightBlue};
                }
            }

            &:focus,
            &:focus-visible {
                outline: none;
                border: 1px solid ${themeColors.primary.white};
                box-shadow: ${elevation.focus};
            }

            &:disabled {
                svg {
                    path {
                        fill: ${themeBackgrounds.darkGray};
                    }
                }
                ${TypographyStyle} {
                    color: ${themeBackgrounds.darkGray};
                }
            }
        `,
        typographyProps: {
            color: themeColors.primary.white,
            underline: false,
            size: fontSizes.small,
            lineHeight: lineHeights.defaultMedium,
        },
    },
    checkAvailabilityModal: {
        sizeVariant: "large",
    },
};

const styles = checkAvailabilityButtonStyles;

const CheckAvailabilityButton: React.FC<WidgetProps> = () => {
    const [availabilityModalIsOpen, setAvailabilityModalIsOpen] = useState(false);

    const buttonClickHandler = () => {
        setAvailabilityModalIsOpen(true);
    };

    const modalCloseHandler = () => {
        setAvailabilityModalIsOpen(false);
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <Button
                {...styles.checkAvailabilityButton}
                onClick={buttonClickHandler}
                data-test-selector="common_checkAvailabilityButton"
            >
                {translate("Check Availability")}
            </Button>
            <Modal
                headline={translate("Check Availability")}
                isOpen={availabilityModalIsOpen}
                handleClose={modalCloseHandler}
                {...styles.checkAvailabilityModal}
            >
                {availabilityModalIsOpen && (
                    <CheckAvailabilityProductSelector
                        data-test-selector="pna_productSelector_global"
                    ></CheckAvailabilityProductSelector>
                )}

            </Modal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: CheckAvailabilityButton,
    definition: {
        group: "Common",
        displayName: "Check Availability Link",
    },
};

export default widgetModule;
